import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePrefState } from './usePrefState';

/**
 * Resolver the caller supplies so this hook stays free of any nav import (shared/ must not
 * reach into app/). Given a stored value that is a LEGACY route path, return the nav-item id
 * that owns it, or {@code null} to drop it. {@link Sidebar} passes a resolver over the
 * demo-aware nav tree + the PH10 extras list.
 */
export type LegacyFavoriteResolver = (storedValue: string) => string | null;

/**
 * slice-3641: a stored favourite is a NAV ITEM ID (the PB menu id, unique per leaf) — it used
 * to be the route path, which broke two ways on the demo instance (Chris 2026-09-14):
 *
 * <ul>
 *   <li>the demo-only leaves (RF Simulator, Warehouse Status, the Slingshot labor reports) are
 *       injected into the tree at render time, so a route-keyed lookup against the static PB
 *       tree found nothing and the starred row never appeared in Favorites;</li>
 *   <li>six PB menu leaf pairs share ONE route (Tag Order Search / Direct-Bulk Tag Order
 *       Search among them), so the route-keyed index could only remember one of them and the
 *       favourite rendered the sibling's caption.</li>
 * </ul>
 *
 * <p>Ordered by add-time, then by the operator's own drag-and-drop (slice-3651 {@code move});
 * the stored array IS the display order.
 *
 * @param resolveLegacy converts a pre-slice-3641 route-path entry to an id. Omit it (tests,
 *   any future consumer without a nav tree) and the stored list is used verbatim — never
 *   silently emptied.
 */
export function useFavorites(resolveLegacy?: LegacyFavoriteResolver) {
  const [stored, setStored] = usePrefState<string[]>('nav.favorites', []);

  const list = useMemo(
    () => (resolveLegacy ? migrateFavorites(stored, resolveLegacy) : stored),
    [stored, resolveLegacy],
  );

  // Write the migrated list back ONCE per change so the legacy shape doesn't get re-migrated on
  // every mount (and so a route that no longer exists stops being carried around). Guarded on
  // deep equality, not identity: migrateFavorites returns a new array every call.
  const lastWritten = useRef<string | null>(null);
  useEffect(() => {
    if (!resolveLegacy) return;
    if (sameList(list, stored)) return;
    const key = JSON.stringify(list);
    if (lastWritten.current === key) return;
    lastWritten.current = key;
    setStored(list);
  }, [list, stored, resolveLegacy, setStored]);

  const isFavorite = useCallback((id: string) => list.includes(id), [list]);

  const toggle = useCallback(
    (id: string) => {
      setStored((prev) => {
        const current = resolveLegacy ? migrateFavorites(prev, resolveLegacy) : prev;
        return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      });
    },
    [setStored, resolveLegacy],
  );

  /**
   * slice-3651: drop {@code id} immediately before/after {@code targetId} (Chris 2026-09-15:
   * "for favorites - can we make it so that users can drag and choose the position/order").
   * The stored list IS the display order, so this is the whole persistence story.
   */
  const move = useCallback(
    (id: string, targetId: string, place: 'before' | 'after') => {
      setStored((prev) => {
        const current = resolveLegacy ? migrateFavorites(prev, resolveLegacy) : prev;
        return reorderFavorites(current, id, targetId, place);
      });
    },
    [setStored, resolveLegacy],
  );

  const clear = useCallback(() => setStored([]), [setStored]);

  return { favorites: list, isFavorite, toggle, move, clear };
}

/**
 * Pure reorder, exported for tests: returns a new list with {@code id} placed immediately
 * before/after {@code targetId}. Returns the input untouched when either id is missing or the
 * two are the same, so a no-op drop never dirties the pref.
 */
export function reorderFavorites(
  list: readonly string[],
  id: string,
  targetId: string,
  place: 'before' | 'after',
): string[] {
  if (id === targetId || !list.includes(id) || !list.includes(targetId)) return [...list];
  const without = list.filter((x) => x !== id);
  const at = without.indexOf(targetId) + (place === 'after' ? 1 : 0);
  return [...without.slice(0, at), id, ...without.slice(at)];
}

/**
 * Pure migration, exported for tests. Values that are already ids (anything not starting with
 * '/') pass through untouched; a legacy route path is handed to {@code resolveLegacy} and is
 * DROPPED when that returns null (the route no longer belongs to any menu leaf). Duplicates
 * that collapse onto one id (both halves of a shared-route pair were starred) are de-duped,
 * keeping the first.
 */
export function migrateFavorites(
  list: readonly string[],
  resolveLegacy: LegacyFavoriteResolver,
): string[] {
  const out: string[] = [];
  for (const value of list) {
    const id = value.startsWith('/') ? resolveLegacy(value) : value;
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
